import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styles from './service.module.css';
import { useHasPermission, useHasAnyPermission } from '../../contexts/auth';

export const Service = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const canViewJobIssues = useHasPermission("service.view_jobissue");
  const canViewJobServices = useHasPermission("service.view_jobservice");
  const canViewJobs = useHasAnyPermission(["service.view_job", "service.view_own_job"]);
  const canViewParts = useHasPermission("service.view_part");
  const canViewEquipmentGroups = useHasPermission("service.view_equipmentgroup");
  const canViewServiceReminders = useHasPermission("reminders.view_reminder");
  const canViewExternalServices = useHasPermission("service.view_externalservice");
  const canViewShipments = useHasPermission("shipping.view_shipment");

  const pathname = location.pathname;
  const basePath = "/service";
  const isJobDetailsRoute = Boolean(matchPath('/service/jobs/:id/*', pathname))
    || Boolean(matchPath('/service/jobs/:id', pathname));

  const visibleTabs = [
    ...(canViewJobs ? [{ key: 'jobs', path: `${basePath}/jobs` }] : []),
    ...(canViewParts ? [{ key: 'stock-status', path: `${basePath}/stock-status` }] : []),
    ...(canViewShipments ? [{ key: 'shipments', path: `${basePath}/shipments` }] : []),
    ...(canViewExternalServices
      ? [{ key: 'external-services', path: `${basePath}/external-services` }]
      : []),
    ...(canViewServiceReminders
      ? [{ key: 'upcoming-services', path: `${basePath}/upcoming-services` }]
      : []),
    ...(canViewJobIssues ? [{ key: 'issues-catalog', path: `${basePath}/issues-catalog` }] : []),
    ...(canViewJobServices ? [{ key: 'services-catalog', path: `${basePath}/services-catalog` }] : []),
    ...(canViewEquipmentGroups || canViewJobs ? [{ key: 'statistics', path: `${basePath}/statistics` }] : []),
  ];

  // If no tabs are visible, return null
  if (visibleTabs.length === 0) {
    return null;
  }

  const getTabValue = () => {
    if (pathname.includes('/upcoming-services')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'upcoming-services');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/shipments')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'shipments');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/stock-status')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'stock-status');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/issues-catalog')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'issues-catalog');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/external-services')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'external-services');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/services-catalog')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'services-catalog');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/jobs')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'jobs');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/statistics')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'statistics');
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  return (
    <Box className={styles.container}>
      {!isJobDetailsRoute && (
        <Tabs
          value={getTabValue()}
          variant="scrollable"
          scrollButtons="auto"
        >
          {visibleTabs.map((tab) => {
            const labels: Record<string, string> = {
              jobs: t("Jobs"),
              "upcoming-services": t("Upcoming services"),
              "stock-status": t("Stock Status"),
              shipments: t("Shipments"),
              "issues-catalog": t("Issues Catalog"),
              "services-catalog": t("Services Catalog"),
              "external-services": t("External services"),
              statistics: t("Statistics"),
            };
            return (
              <Tab
                key={tab.key}
                label={labels[tab.key]}
                onClick={() => navigate(tab.path)}
              />
            );
          })}
        </Tabs>
      )}
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
